'use client';

import type { ElementType, HTMLAttributes, ReactNode } from 'react';
import { createElement } from 'react';
import { cx } from '../utils/cx';
import { mergePresetClasses } from '../utils/mergePresetClasses';

export type BoxAs =
  | 'div'
  | 'span'
  | 'aside'
  | 'nav'
  | 'main'
  | 'section'
  | 'header'
  | 'footer'
  | 'ul'
  | 'li'
  | 'strong'
  | 'label'
  | 'input'
  | 'style';
export type BoxPreset = 'default' | 'fixedFooter';

export interface BoxProps extends HTMLAttributes<HTMLElement> {
  as?: BoxAs;
  preset?: BoxPreset;
  text?: string;
  ariaLabel?: string;
  ariaHidden?: boolean;
  htmlFor?: string;
  type?: string;
  children?: ReactNode;
}

function presetClass(preset: BoxPreset): string {
  switch (preset) {
    case 'fixedFooter':
      return 'fixed inset-x-0 bottom-0 z-50 bg-gradient-to-b from-transparent to-[#d2e0f8] px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]';
    case 'default':
    default:
      return '';
  }
}

const ARIA_ATTRIBUTE_ALIASES: Record<string, string> = {
  ariaActivedescendant: 'aria-activedescendant',
  ariaAtomic: 'aria-atomic',
  ariaAutocomplete: 'aria-autocomplete',
  ariaBusy: 'aria-busy',
  ariaChecked: 'aria-checked',
  ariaColcount: 'aria-colcount',
  ariaColindex: 'aria-colindex',
  ariaColspan: 'aria-colspan',
  ariaControls: 'aria-controls',
  ariaCurrent: 'aria-current',
  ariaDescribedby: 'aria-describedby',
  ariaDetails: 'aria-details',
  ariaDisabled: 'aria-disabled',
  ariaErrormessage: 'aria-errormessage',
  ariaExpanded: 'aria-expanded',
  ariaHaspopup: 'aria-haspopup',
  ariaHidden: 'aria-hidden',
  ariaInvalid: 'aria-invalid',
  ariaKeyshortcuts: 'aria-keyshortcuts',
  ariaLabel: 'aria-label',
  ariaLabelledby: 'aria-labelledby',
  ariaLevel: 'aria-level',
  ariaLive: 'aria-live',
  ariaMultiline: 'aria-multiline',
  ariaMultiselectable: 'aria-multiselectable',
  ariaOrientation: 'aria-orientation',
  ariaPlaceholder: 'aria-placeholder',
  ariaPosinset: 'aria-posinset',
  ariaPressed: 'aria-pressed',
  ariaReadonly: 'aria-readonly',
  ariaRelevant: 'aria-relevant',
  ariaRequired: 'aria-required',
  ariaRoleDescription: 'aria-roledescription',
  ariaRowcount: 'aria-rowcount',
  ariaRowindex: 'aria-rowindex',
  ariaRowspan: 'aria-rowspan',
  ariaSelected: 'aria-selected',
  ariaSetsize: 'aria-setsize',
  ariaSort: 'aria-sort',
  ariaValueMax: 'aria-valuemax',
  ariaValueMin: 'aria-valuemin',
  ariaValueNow: 'aria-valuenow',
  ariaValueText: 'aria-valuetext',
  'aria-value-max': 'aria-valuemax',
  'aria-value-min': 'aria-valuemin',
  'aria-value-now': 'aria-valuenow',
  'aria-value-text': 'aria-valuetext',
};

function normalizeAriaAttributes(
  props: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...props };
  for (const [camelCase, kebabCase] of Object.entries(ARIA_ATTRIBUTE_ALIASES)) {
    if (
      Object.prototype.hasOwnProperty.call(next, camelCase) &&
      !Object.prototype.hasOwnProperty.call(next, kebabCase)
    ) {
      next[kebabCase] = next[camelCase];
    }
    delete next[camelCase];
  }
  return next;
}

const RANGE_ARIA_ATTRIBUTES = [
  'aria-valuemin',
  'aria-valuemax',
  'aria-valuenow',
  'aria-valuetext',
] as const;

const RANGE_ARIA_ROLES = new Set([
  'meter',
  'progressbar',
  'scrollbar',
  'slider',
  'spinbutton',
]);

function sanitizeRangeAriaAttributes(
  as: BoxAs,
  props: Record<string, unknown>,
): Record<string, unknown> {
  if (as === 'input') return props;

  const role = typeof props.role === 'string' ? props.role.toLowerCase() : '';
  if (RANGE_ARIA_ROLES.has(role)) return props;

  const next = { ...props };
  for (const attribute of RANGE_ARIA_ATTRIBUTES) {
    delete next[attribute];
  }
  return next;
}

function normalizeUncontrolledValue(
  as: BoxAs,
  props: Record<string, unknown>,
): Record<string, unknown> {
  if (!['input', 'select', 'textarea'].includes(as)) return props;
  if (!Object.prototype.hasOwnProperty.call(props, 'value')) return props;

  const isControlled =
    typeof props.onChange === 'function' ||
    Object.prototype.hasOwnProperty.call(props, 'readOnly');

  if (isControlled) {
    if (props.value === undefined || props.value === null) {
      return { ...props, value: '' };
    }
    return props;
  }

  if (Object.prototype.hasOwnProperty.call(props, 'defaultValue')) {
    const { value: _value, ...withoutValue } = props;
    return withoutValue;
  }
  const { value, ...withoutValue } = props;
  return { ...withoutValue, defaultValue: value };
}

function normalizeControlledChecked(
  as: BoxAs,
  props: Record<string, unknown>,
): Record<string, unknown> {
  if (as !== 'input' || !Object.prototype.hasOwnProperty.call(props, 'checked')) {
    return props;
  }

  const inputType = typeof props.type === 'string' ? props.type.toLowerCase() : '';
  const isControlled =
    typeof props.onChange === 'function' ||
    Object.prototype.hasOwnProperty.call(props, 'readOnly');

  if (!isControlled || !['checkbox', 'radio'].includes(inputType)) return props;
  if (props.checked === undefined || props.checked === null) {
    return { ...props, checked: false };
  }
  return props;
}

export function Box({
  as = 'div',
  preset = 'default',
  ariaLabel,
  ariaHidden,
  text,
  className,
  children,
  ...rest
}: BoxProps) {
  const content = children ?? text;
  const mergedClassName = mergePresetClasses(presetClass(preset), className);

  const rawProps: Record<string, unknown> = {
    ...rest,
    ...(typeof ariaHidden === 'boolean' ? { ariaHidden } : {}),
    ...(ariaLabel ? { ariaLabel } : {}),
    className: cx(mergedClassName),
  };
  const elementProps = normalizeControlledChecked(
    as,
    normalizeUncontrolledValue(
      as,
      sanitizeRangeAriaAttributes(as, normalizeAriaAttributes(rawProps)),
    ),
  );

  if (as === 'input') {
    return createElement(as, elementProps);
  }

  return createElement(as as ElementType, elementProps, content);
}

Box.displayName = 'Box';